import { useMemo } from "react";
import { skipToken } from "@reduxjs/toolkit/query/react";

import { useGetCylindersSummaryQuery, useGetVolumeOffersQuery } from "./homeApi";
import {
  cheapestForSuppliers,
  groupOffersByVolume,
} from "../search/suppliersMappers";
import { quantiseCoordinates, radiusToMeters } from "../search/supplierFilters";

const useVolumeOffers = ({
  coordinates,
  radiusKm,
  containerOptionId,
  maxVolume,
  supplierCount,
  feedSupplierIds = null,
  feedIsEmpty = false,
}) => {
  const cell = useMemo(() => quantiseCoordinates(coordinates), [coordinates]);
  const distanceMeters = radiusToMeters(radiusKm);

  const summary = useGetCylindersSummaryQuery();

  const offersArgs = cell
    ? {
        latitude: cell.latitude,
        longitude: cell.longitude,
        distanceMeters,
        containerOptionId: containerOptionId || null,
      }
    : skipToken;

  const offers = useGetVolumeOffersQuery(offersArgs);

  const cards = useMemo(() => {
    if (feedIsEmpty) return [];

    const cylinders = summary.currentData || [];
    if (!cylinders.length) return [];

    const offersFailed = offers.isError;
    const groups = groupOffersByVolume(offers.currentData?.rows || []);

    return cylinders
      .filter((cylinder) => Number(cylinder.volume) <= maxVolume)
      .map((cylinder) => {
        const volume = Number(cylinder.volume);
        const group = groups.get(volume);

        if (!group) {
          if (!offersFailed) return null;

          return {
            volume,
            imageUrl: cylinder.imageUrl,
            supplierCount: Math.min(cylinder.supplierCount || 0, supplierCount),
            supplierIds: [],
            price: cylinder.priceFrom?.value ?? null,
            currency: cylinder.priceFrom?.currency || "GHS",
            estimated: true,
          };
        }

        const supplierIds = feedSupplierIds
          ? Array.from(group.supplierIds).filter((id) => feedSupplierIds.has(id))
          : Array.from(group.supplierIds);

        const price = feedSupplierIds
          ? cheapestForSuppliers(group, supplierIds)
          : group.price;

        return {
          volume,
          imageUrl: cylinder.imageUrl,
          supplierCount: supplierIds.length,
          supplierIds,
          price: price ?? cylinder.priceFrom?.value ?? null,
          currency: group.currency,
          estimated: false,
        };
      })
      .filter((card) => card && card.supplierCount > 0);
  }, [
    summary.currentData,
    offers.currentData,
    offers.isError,
    maxVolume,
    supplierCount,
    feedSupplierIds,
    feedIsEmpty,
  ]);

  return {
    cards,
    truncated: Boolean(offers.currentData?.truncated),
    isFetching: summary.isFetching || offers.isFetching,
    refetch: cell ? offers.refetch : () => {},
  };
};

export default useVolumeOffers;
